import React from 'react';
import { Shield, Home, ArrowLeft, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

const AccessDenied = ({ onNavigate, requiredRole = 'admin', sectionName = 'cette section', missingFeature = null, currentPlan = null, deniedSection = null, isSuperAdmin = false }) => {
  const { t } = useTranslation();

  const getRoleLabel = (role) => {
    if (role === 'admin') return t('access_denied.role_admin');
    if (role === 'manager') return t('access_denied.role_manager');
    if (role === 'admin_or_manager') return t('access_denied.role_admin_or_manager');
    return role;
  };

  const getFeatureLabel = (feature) => {
    const key = `access_denied.feature_${feature}`;
    const translated = t(key);
    return translated !== key ? translated : feature;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      overflow: 'hidden',
      zIndex: 10000
    }}>
      {/* Effet de fond animé */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: 'radial-gradient(circle, rgba(239, 68, 68, 0.1) 0%, transparent 70%)',
        animation: 'pulse 4s ease-in-out infinite',
        zIndex: 0
      }} />

      <div style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: '600px',
        width: '100%',
        textAlign: 'center'
      }}>
        {/* Card principale avec glassmorphism */}
        <div style={{
          background: 'rgba(15, 23, 42, 0.8)',
          backdropFilter: 'blur(30px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '24px',
          padding: '48px 32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(239, 68, 68, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Overlay gradient */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 1
          }} />

          <div style={{ position: 'relative', zIndex: 2 }}>
            {/* Icône */}
            <div style={{
              width: '100px',
              height: '100px',
              margin: '0 auto 32px',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}>
              <Shield size={48} color="#ef4444" strokeWidth={2.5} />
            </div>

            {/* Titre */}
            <h1 style={{
              fontSize: 'clamp(28px, 5vw, 36px)',
              fontWeight: 700,
              color: '#ffffff',
              marginBottom: '16px',
              letterSpacing: '-0.5px',
              lineHeight: 1.2
            }}>
              {t('access_denied.title')}
            </h1>

            {/* Message */}
            <p style={{
              fontSize: 'clamp(16px, 2.5vw, 18px)',
              color: 'rgba(255, 255, 255, 0.8)',
              marginBottom: '32px',
              lineHeight: 1.6,
              fontWeight: 400
            }}>
              {deniedSection === 'billing' ? (
                <>⚠️ {t('access_denied.billing_only_admin_manager')}</>
              ) : missingFeature ? (
                <>
                  ⚠️ {t('access_denied.module_not_available', { name: getFeatureLabel(missingFeature) })}
                </>
              ) : (
                <>
                  ⚠️ {t('access_denied.no_permission', { section: sectionName })}
                </>
              )}
            </p>

            {/* Alerte avec style glassmorphism */}
            <div style={{
              background: 'rgba(239, 68, 68, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '16px',
              padding: '20px 24px',
              marginBottom: '32px',
              boxShadow: '0 4px 16px rgba(239, 68, 68, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}>
                <AlertTriangle size={20} color="#ef4444" />
                <p style={{
                  fontSize: '16px',
                  color: '#fca5a5',
                  margin: 0,
                  fontWeight: 500
                }}>
                  {deniedSection === 'billing' ? (
                    <>🔒 {t('access_denied.section_reserved', { role: getRoleLabel(requiredRole) })}</>
                  ) : missingFeature ? (
                    <>
                      📦 {t('access_denied.module_plan_part', { plan: currentPlan || 'Business ou Enterprise' })}
                      {currentPlan && <> {t('access_denied.your_plan', { plan: currentPlan })}</>}
                    </>
                  ) : (
                    <>
                      🔒 {t('access_denied.section_reserved', { role: getRoleLabel(requiredRole) })}
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Bouton Upgrade si feature manquante */}
            {missingFeature && (
              <button
                onClick={() => onNavigate('billing-plans')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  backdropFilter: 'blur(10px)',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                  margin: '0 auto 16px',
                  width: 'fit-content'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 24px rgba(16, 185, 129, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
                }}
              >
                🚀 {t('access_denied.choose_plan')}
              </button>
            )}

            {/* Actions */}
            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              {deniedSection === 'billing' && isSuperAdmin && (
                <button
                  onClick={() => onNavigate('super-admin')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 16px rgba(220, 38, 38, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 24px rgba(220, 38, 38, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(220, 38, 38, 0.3)';
                  }}
                >
                  <Shield size={20} />
                  {t('access_denied.go_super_admin')}
                </button>
              )}
              <button
                onClick={() => onNavigate('home')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 28px',
                  background: 'rgba(59, 130, 246, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.boxShadow = '0 6px 24px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
                }}
              >
                <Home size={20} />
                {t('access_denied.back_home')}
              </button>

              <button
                onClick={() => window.history.back()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 28px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: 'rgba(255, 255, 255, 0.9)',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <ArrowLeft size={20} />
                {t('access_denied.back')}
              </button>
            </div>

            {/* Informations supplémentaires */}
            <div style={{
              marginTop: '32px',
              paddingTop: '32px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.6)',
                margin: 0,
                fontWeight: 400
              }}>
                💡 {t('access_denied.contact_admin')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
        }
      `}</style>
    </div>
  );
};

export default AccessDenied;






















